import React, { useEffect, useRef, useState } from 'react';
import { ToggleRow } from './ui';
import spotifyService from '../../../services/spotifyService';

/**
 * YOUR ANTHEM · SPOTIFY — search at 2+ characters, 300ms debounce, `searchTracks(q, 8)`, then
 * `getTrack` on pick to recover a 30-second preview the search result didn't carry.
 *
 * Saves `{ trackId, name, artist, albumArt, previewUrl, spotifyUrl, autoplay }` — the schema the
 * intake wrote, which `extendedProfile.anthem` and `ProfileAnthem` both still read. Shared by the
 * Portal Editor's VIBE + MUSIC tab and Onboarding v2's step 2; `compact` is onboarding's smaller
 * row (40px art, no autoplay toggle — the default is on and the editor owns the exception).
 */
export default function AnthemPicker({ value, onChange, compact = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return undefined; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      const r = await spotifyService.searchTracks(q, 8);
      setResults(r.success ? (r.data || []) : []);
      setSearching(false);
    }, 300);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [query]);

  const pick = async (t) => {
    setResolving(true);
    let previewUrl = t.previewUrl || null;
    try {
      const r = await spotifyService.getTrack(t.id);
      if (r.success && r.data?.previewUrl) previewUrl = r.data.previewUrl;
    } catch { /* fall back to the embed */ }
    setResolving(false);
    onChange({ trackId: t.id, name: t.name, artist: t.artist, albumArt: t.albumArtUrl || null, previewUrl, spotifyUrl: t.spotifyUrl || null, autoplay: value?.autoplay ?? true });
    setQuery('');
    setResults([]);
  };

  if (value?.trackId) {
    const art = compact ? 52 : 56;
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 10, borderRadius: 22, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A' }} data-testid="anthem-picked">
          {value.albumArt
            ? <img src={value.albumArt} alt="" referrerPolicy="no-referrer" style={{ flexShrink: 0, width: art, height: art, borderRadius: 14, objectFit: 'cover', display: 'block' }} />
            : <span aria-hidden="true" style={{ flexShrink: 0, width: art, height: art, borderRadius: 14, background: '#1DB954' }} />}
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value.name}</span>
            <span style={{ fontSize: 12, color: '#8A8A8A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value.artist}</span>
            {!value.previewUrl && <span style={{ fontSize: 10, lineHeight: 1.5, color: '#6B6B6B' }}>No 30-sec preview for this track. Visitors get the Spotify player instead (tap to play).</span>}
          </span>
          <button type="button" onClick={() => onChange(null)} className="pk-btn pe-hover-white" style={{ flexShrink: 0, height: 34, padding: '0 14px', background: '#1F1F1F', color: '#fff', fontSize: compact ? 10 : 9, fontWeight: 800, letterSpacing: '.14em' }}>{compact ? 'CHANGE' : 'REMOVE'}</button>
        </div>
        {!compact && (
          <ToggleRow on={value.autoplay !== false} onToggle={() => onChange({ ...value, autoplay: value.autoplay === false })} label="Autoplay on my page" note="(starts on a visitor’s first tap)" />
        )}
      </>
    );
  }

  const q = query.trim();
  return (
    <>
      <span className="pe-in-wrap" style={{ display: 'flex', alignItems: 'center', gap: 10, height: compact ? 50 : 48, padding: '0 20px', borderRadius: 999, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A', boxSizing: 'border-box' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B6B6B" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search a song or artist…" aria-label="Search Spotify" data-testid="anthem-query" style={{ flex: 1, minWidth: 0, border: 'none', background: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 14, color: '#fff' }} />
      </span>
      {(searching || resolving) && <span style={{ fontSize: 11, color: '#6B6B6B' }}>{resolving ? 'ADDING YOUR ANTHEM…' : 'SEARCHING…'}</span>}
      {results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 300, overflowY: 'auto', padding: 6, borderRadius: 20, background: '#000', boxShadow: 'inset 0 0 0 1px #2A2A2A' }} data-testid="anthem-results">
          {results.map((t) => (
            <button key={t.id} type="button" onClick={() => pick(t)} className="pk-btn pk-row-hover" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 10px 6px 6px', borderRadius: 14, background: 'transparent', color: '#fff', textAlign: 'left', whiteSpace: 'normal', justifyContent: 'flex-start' }}>
              {t.albumArtUrl
                ? <img src={t.albumArtUrl} alt="" referrerPolicy="no-referrer" style={{ flexShrink: 0, width: compact ? 40 : 44, height: compact ? 40 : 44, borderRadius: 10, objectFit: 'cover', display: 'block' }} />
                : <span aria-hidden="true" style={{ flexShrink: 0, width: compact ? 40 : 44, height: compact ? 40 : 44, borderRadius: 10, background: '#1A1A1A' }} />}
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                <span style={{ fontSize: 11, color: '#8A8A8A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.artist}</span>
              </span>
              {!compact && <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: '#8A8A8A' }}>PICK</span>}
            </button>
          ))}
        </div>
      )}
      {q.length >= 2 && !searching && !results.length && <span style={{ fontSize: 11, color: '#6B6B6B' }}>NO TRACKS FOUND.</span>}
    </>
  );
}
